import React, { useMemo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { services, additionalServices, serviceHighlights } from '../data/servicesData';

const Services = () => {
  const [activeTab, setActiveTab] = useState('germany');

  // Memoize data for performance
  const memoizedServices = useMemo(() => services, []);
  const memoizedAdditionalServices = useMemo(() => additionalServices, []);
  const memoizedHighlights = useMemo(() => serviceHighlights, []);

  // Get active country's services
  const activeCountryServices = useMemo(
    () => memoizedServices.find(s => s.id === activeTab) || memoizedServices[0],
    [memoizedServices, activeTab]
  );

  // Scroll progress indicator effect with animations
  useEffect(() => {
    const handleScroll = () => {
      const scrollProgress = document.getElementById('services-scroll-progress');
      if (scrollProgress) {
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight - windowHeight;
        const scrolled = window.scrollY;
        const progress = (scrolled / documentHeight) * 100;
        scrollProgress.style.width = `${Math.min(progress, 100)}%`;
        scrollProgress.setAttribute('aria-valuenow', Math.min(progress, 100).toFixed(0));
      }

      // Intersection Observer for animations
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
            entry.target.style.opacity = '1';
          }
        });
      }, observerOptions);

      // Observe elements
      const animatedElements = document.querySelectorAll('.animate-on-scroll');
      animatedElements.forEach((el) => observer.observe(el));

      return () => {
        animatedElements.forEach((el) => observer.unobserve(el));
      };
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Scroll Progress Indicator */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50" role="progressbar" aria-label="Page scroll progress">
        <div className="h-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all duration-300 ease-out" style={{width: '0%'}} id="services-scroll-progress" aria-valuenow={0} aria-valuemin={0} aria-valuemax={100}></div>
      </div>
      <Helmet>
        <title>Our Services - Study in 5 Countries | Ravana Migration</title>
        <meta name="description" content="Comprehensive migration services for Germany, Canada, Australia, UK, and New Zealand. Bachelor's, Master's, PhD programs, work visas, and more." />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 sm:py-16 lg:py-20">
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float"></div>
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float" style={{animationDelay: '2s'}}></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center animate-fade-in-up" style={{opacity: 0}}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 leading-tight">
                <span className="relative inline-block">
                  <span className="relative z-10">Flight Plan™ Alliance Partner</span>
                  <svg className="absolute -bottom-2 left-0 w-full h-6 text-blue-400 opacity-30" viewBox="0 0 200 20" fill="currentColor">
                    <path d="M0 15 Q 50 5 100 15 T 200 15 L 200 20 L 0 20 Z" />
                  </svg>
                </span>{' '}
                Services
              </h1>
              <div className="rule mx-auto mb-4"></div>
              <p className="text-xl sm:text-2xl text-gray-700 mb-4 max-w-3xl mx-auto font-medium">
                One partner. Five countries. Unlimited possibilities.
              </p>
              <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Comprehensive visa and migration services across Germany, Canada, Australia, the United Kingdom, and New Zealand.
              </p>

              {/* Quick stats */}
              <div className="flex flex-wrap justify-center gap-6 sm:gap-8 text-sm text-gray-600 mt-8">
                <div className="flex items-center gap-2 bg-white/60 px-4 py-2 rounded-full backdrop-blur-sm">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                  </svg>
                  <span className="font-semibold">500+ Students</span>
                </div>
                <div className="flex items-center gap-2 bg-white/60 px-4 py-2 rounded-full backdrop-blur-sm">
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-semibold">99% Success Rate</span>
                </div>
                <div className="flex items-center gap-2 bg-white/60 px-4 py-2 rounded-full backdrop-blur-sm">
                  <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                  </svg>
                  <span className="font-semibold">5 Countries</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Service Highlights */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center mb-8 animate-on-scroll" style={{opacity: 0}}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Why Choose Our Services?</h2>
            <div className="rule mx-auto mb-4"></div>
            <p className="text-lg sm:text-xl text-gray-600">Trusted by students worldwide</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 animate-on-scroll" style={{opacity: 0}} role="list" aria-label="Service highlights">
            {memoizedHighlights.map((highlight, index) => (
              <div key={index} className="card text-center p-4 md:p-6 hover-lift group" role="listitem">
                <div className="text-4xl md:text-5xl mb-3 md:mb-4 group-hover:scale-110 transition-transform" aria-hidden="true">{highlight.icon}</div>
                <div className="text-xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2 group-hover:scale-105 transition-transform">
                  {highlight.value}
                </div>
                <div className="text-sm font-semibold text-gray-900 mb-1 md:mb-2">{highlight.title}</div>
                <div className="text-xs text-gray-600">{highlight.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Services by Country - Tabbed Interface */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="animate-on-scroll" style={{opacity: 0}}>
            {/* Tab Navigation */}
            <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-8 bg-white/50 p-2 rounded-2xl backdrop-blur-sm shadow-lg">
              {memoizedServices.map((service) => (
                <button
                  key={service.id}
                  onClick={() => setActiveTab(service.id)}
                  className={`flex items-center gap-2 px-4 md:px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
                    activeTab === service.id
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                  }`}
                  aria-label={`View services for ${service.country}`}
                  aria-pressed={activeTab === service.id}
                >
                  <span className="text-2xl md:text-3xl" aria-hidden="true">{service.flag}</span>
                  <span className="text-sm md:text-base">{service.country}</span>
                </button>
              ))}
            </div>

            {/* Active Country Content */}
            <div className="rounded-3xl overflow-hidden shadow-lg">
              <div className={`bg-gradient-to-br ${activeCountryServices.color} border-2 ${activeCountryServices.borderColor} hover-lift`}>
                <div className="p-6 md:p-8">
                  {/* Country Header */}
                  <div className="flex items-center justify-center gap-4 md:gap-6 mb-6" aria-label={`Services for ${activeCountryServices.country}`}>
                    <span className="text-5xl sm:text-6xl md:text-7xl transform hover:scale-110 transition-transform" aria-hidden="true">{activeCountryServices.flag}</span>
                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">{activeCountryServices.country}</h2>
                  </div>

                  {/* Highlight for Canada */}
                  {activeCountryServices.highlight && (
                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-xl p-4 md:p-6 mb-6 text-center shadow-md">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <svg className="w-5 h-5 md:w-6 md:h-6 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <p className="text-sm md:text-lg font-bold text-gray-900">
                          {activeCountryServices.highlight}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Programs Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6" role="list" aria-label={`Available programs for ${activeCountryServices.country}`}>
                    {activeCountryServices.programs.map((program) => (
                      <div key={program.id} className="card bg-white hover-lift group relative overflow-hidden" role="listitem">
                        {/* Gradient overlay on hover */}
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/0 to-purple-50/0 group-hover:from-blue-50/30 group-hover:to-purple-50/30 transition-all duration-300"></div>

                        <div className="relative">
                          <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3 group-hover:text-blue-600 transition-colors">{program.title}</h3>
                          <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4 leading-relaxed">{program.description}</p>
                          <div className="space-y-1 md:space-y-2" role="list" aria-label={`Features for ${program.title}`}>
                            {program.features.map((feature, idx) => (
                              <div key={idx} className="flex items-start gap-2 md:gap-3" role="listitem">
                                <svg className="w-4 h-4 md:w-5 md:h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <span className="text-xs md:text-sm text-gray-700">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Services */}
        <div className="bg-gray-50 py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 md:mb-12 animate-on-scroll" style={{opacity: 0}}>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Additional Support Services</h2>
              <div className="rule mx-auto mb-4"></div>
              <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive support services included with all our packages
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6 animate-on-scroll" style={{opacity: 0}} role="list" aria-label="Additional support services">
              {memoizedAdditionalServices.map((service, index) => (
                <div key={service.id} className="card card-hover text-center group hover-lift" role="listitem">
                  <div className="text-4xl md:text-6xl mb-3 md:mb-4 group-hover:scale-110 transition-transform" aria-hidden="true">{service.icon}</div>
                  <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2 group-hover:text-blue-600 transition-colors">{service.title}</h3>
                  <p className="text-xs md:text-sm text-gray-600">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="relative overflow-hidden rounded-3xl shadow-2xl animate-on-scroll" style={{opacity: 0}}>
            {/* Animated background */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700"></div>
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-40 -right-40 w-80 h-80 bg-white opacity-10 rounded-full mix-blend-overlay filter blur-xl animate-float"></div>
              <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white opacity-10 rounded-full mix-blend-overlay filter blur-xl animate-float" style={{animationDelay: '2s'}}></div>
            </div>

            <div className="relative text-center py-12 md:py-16 px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6 leading-tight">
                Ready to Start Your Journey?
              </h2>
              <div className="rule mx-auto mb-4 md:mb-6 bg-white/30"></div>
              <p className="text-lg sm:text-xl text-white mb-8 md:mb-10 max-w-3xl mx-auto leading-relaxed">
                Choose the perfect package for your study abroad goals
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/pricing" className="btn bg-white text-blue-600 hover:bg-gray-100 text-lg px-6 md:px-8 py-3 md:py-4 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 group">
                  <span>View Pricing</span>
                  <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
                <Link to="/choice" className="btn btn-outline text-white border-white/80 hover:bg-white hover:text-blue-600 text-lg px-6 md:px-8 py-3 md:py-4 backdrop-blur-sm transform hover:scale-105 transition-all duration-300 group">
                  <svg className="w-5 h-5 mr-2 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span>Consult Now</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Mobile CTA - respect safe area and chat launcher */}
      <div
        className="fixed left-4 right-24 z-50 md:hidden pb-safe"
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + var(--chat-launcher-offset, 80px))' }}
      >
        <Link
          to="/choice"
          className="btn btn-accent w-full text-center text-lg py-4 shadow-2xl animate-pulse-slow"
          aria-label="Start your free consultation for study abroad services"
        >
          <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Consult an Expert →
        </Link>
      </div>
    </>
  );
};

export default Services;
